import { useState, useEffect, useCallback } from "react";
import { AiOutlineWarning, AiOutlineCheckCircle } from "react-icons/ai";
import { FiEdit } from "react-icons/fi";
import axios from "axios";
import Swal from "sweetalert2";
import { Modal, Col, Form, Row } from "react-bootstrap";
import GuardedModal from "../common/GuardedModal";
import { Button } from "semantic-ui-react";
import { headers } from "../global_vars";
import { CASH_TYPE_FIELD_LABEL } from "../common/relatedLinkLabels";
import AutocompleteSelect from "../common/AutocompleteSelect";
import SelectWithInlineCreate from "../common/SelectWithInlineCreate";
import AddCountryModal from "./create/add_country";
import DateInput from "../common/DateInput";


const UPDATE_CASH = "https://ultima.icsgr.com/api/data_management/cash/";

function toDateInputValue(value) {
  if (!value) return "";
  const str = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  return str;
}

function EditCashModal({
  cash,
  refreshData,
  onUpdated,
  projectOptions,
  lockProject = false,
  lockConsultant = false,
  trigger,
}) {
  const [show, setShow] = useState(false);
  const [project, setProject] = useState("");
  const [country, setCountry] = useState("");
  const [trandate, setTrandate] = useState("");
  const [consultant, setConsultant] = useState("");
  const [kind, setKind] = useState("E");
  const [amountexp, setAmountexp] = useState("");
  const [amountpay, setAmountpay] = useState("");
  const [reason, setReason] = useState("");
  const [projects, setProjects] = useState([]);
  const [countries, setCountries] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [saving, setSaving] = useState(false);
  const [addCountryOpen, setAddCountryOpen] = useState(false);

  const populateFromCash = useCallback(() => {
    if (!cash) return;
    setProject(cash.project?.project_id || "");
    setCountry(cash.country?.country_id || "");
    setTrandate(toDateInputValue(cash.trandate));
    setConsultant(cash.consultant?.consultant_id || "");
    setKind(cash.kind || "E");
    setAmountexp(cash.amountexp != null && cash.amountexp !== "" ? String(cash.amountexp) : "");
    setAmountpay(cash.amountpay != null && cash.amountpay !== "" ? String(cash.amountpay) : "");
    setReason(cash.reason || "");
  }, [cash]);

  const loadDropdownData = useCallback(async () => {
    try {
      const currentHeaders = {
        ...headers,
        Authorization: "Token " + localStorage.getItem("userToken"),
      };

      const countriesRes = await axios.get("https://ultima.icsgr.com/api/regions/all_countries/", {
        headers: currentHeaders,
      });
      const consultantsRes = await axios.get("https://ultima.icsgr.com/api/administration/all_consultants/", {
        headers: currentHeaders,
      });

      let projectsData = [];
      if (Array.isArray(projectOptions)) {
        projectsData = projectOptions;
      } else {
        const projectsRes = await axios.get("https://ultima.icsgr.com/api/data_management/all_projects/", {
          headers: currentHeaders,
        });
        projectsData = projectsRes?.data?.all_projects || [];
      }

      setProjects(projectsData);
      setCountries(countriesRes?.data?.all_countries || []);
      setConsultants(consultantsRes?.data?.all_consultants || []);
    } catch (_e) {
      setProjects([]);
      setCountries([]);
      setConsultants([]);
    }
  }, [projectOptions]);

  useEffect(() => {
    if (show) {
      populateFromCash();
      loadDropdownData();
    }
  }, [show, populateFromCash, loadDropdownData]);

  useEffect(() => {
    if (kind === "E") setAmountpay("");
    else if (kind === "P") setAmountexp("");
  }, [kind]);

  const isProjectValid = project.length > 0;
  const isCountryValid = country.length > 0;
  const isTrandateValid = trandate.length > 0;
  const isConsultantValid = consultant.length > 0;
  const isReasonValid = reason.trim().length >= 1 && reason.trim().length <= 120;
  const isFormValid = isProjectValid && isCountryValid && isTrandateValid && isConsultantValid && isReasonValid;

  const handleOpen = () => {
    populateFromCash();
    setShow(true);
  };

  const handleSave = async () => {
    if (!cash?.cash_id || !isFormValid) return;
    setSaving(true);
    try {
      const currentHeaders = {
        ...headers,
        Authorization: "Token " + localStorage.getItem("userToken"),
      };

      const res = await axios.put(
        `${UPDATE_CASH}${cash.cash_id}/`,
        {
          project_id: project,
          country_id: country,
          trandate,
          consultant_id: consultant,
          kind,
          amountexp: kind === "E" ? (amountexp.trim() || null) : null,
          amountpay: kind === "P" ? (amountpay.trim() || null) : null,
          reason: reason.trim() || null,
        },
        { headers: currentHeaders }
      );

      Swal.fire("Success", "Accounting entry updated", "success");
      setShow(false);
      if (onUpdated) onUpdated(res.data);
      if (refreshData) refreshData();
    } catch (e) {
      const apiMsg =
        e?.response?.data?.errormsg ||
        e?.response?.data?.detail ||
        e?.response?.data?.error ||
        "Failed to update accounting entry";
      Swal.fire("Error", typeof apiMsg === "object" ? JSON.stringify(apiMsg) : apiMsg, "error");
    } finally {
      setSaving(false);
    }
  };

  const defaultTrigger = (
    <button
      type="button"
      onClick={handleOpen}
      title="Edit transaction"
      aria-label="Edit transaction"
      style={{
        padding: "5px 8px",
        border: "1px solid #93ab3c",
        borderRadius: 6,
        background: "#f4f7e8",
        color: "#5a6d24",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        lineHeight: 1,
      }}
    >
      <FiEdit size={16} />
    </button>
  );

  if (!cash?.cash_id) return null;

  return (
    <>
      {trigger ? (
        <span onClick={handleOpen} role="presentation" style={{ display: "inline-flex" }}>
          {trigger}
        </span>
      ) : (
        defaultTrigger
      )}

      <GuardedModal show={show} size="lg" onHide={() => setShow(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit accounting entry {cash.cash_id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="justify-content-md-center">
            <Col>
              <Form>
                <h6 className="mb-3">Basic information</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Project *:</Form.Label>
                      <AutocompleteSelect
                        value={project}
                        onChange={setProject}
                        disabled={lockProject}
                        placeholder="Select project"
                      >
                        {projects.map((item) => (
                          <option key={item.project_id} value={item.project_id}>
                            {item.title}
                          </option>
                        ))}
                      </AutocompleteSelect>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <SelectWithInlineCreate
                      className="mb-3"
                      label="Country"
                      required
                      value={country}
                      onChange={setCountry}
                      placeholderOption="Select country"
                      onAddClick={() => setAddCountryOpen(true)}
                      overviewLinkEntity="country"
                    >
                      {countries.map((item) => (
                        <option key={item.country_id} value={item.country_id} title={item.country_id}>
                          {item.title}
                        </option>
                      ))}
                    </SelectWithInlineCreate>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Transaction date *:</Form.Label>
                      <DateInput
                        onChange={(e) => setTrandate(e.target.value)}
                        value={trandate}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Consultant *:</Form.Label>
                      <AutocompleteSelect
                        value={consultant}
                        onChange={setConsultant}
                        disabled={lockConsultant}
                        placeholder="Select consultant"
                      >
                        {consultants.map((item) => (
                          <option key={item.consultant_id} value={item.consultant_id}>
                            {item.fullname}
                          </option>
                        ))}
                      </AutocompleteSelect>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{CASH_TYPE_FIELD_LABEL}:</Form.Label>
                      <AutocompleteSelect value={kind} onChange={setKind}>
                        <option value="E">Debit</option>
                        <option value="P">Credit</option>
                      </AutocompleteSelect>
                    </Form.Group>
                  </Col>
                </Row>
                {kind === "E" && (
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Amount expense:</Form.Label>
                        <Form.Control
                          type="number"
                          placeholder="e.g., 100.00"
                          onChange={(e) => setAmountexp(e.target.value)}
                          value={amountexp}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}
                {kind === "P" && (
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Amount payment:</Form.Label>
                        <Form.Control
                          type="number"
                          placeholder="e.g., 100.00"
                          onChange={(e) => setAmountpay(e.target.value)}
                          value={amountpay}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}
                <Form.Group className="mb-3">
                  <Form.Label>Reason *:</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Reason for the accounting entry"
                    onChange={(e) => setReason(e.target.value)}
                    value={reason}
                  />
                </Form.Group>
              </Form>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto">
            {!isFormValid ? (
              <ul className="mr-auto" style={{ margin: 0, padding: 0, color: "red" }}>
                {!isProjectValid && <li><AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />Project is required.</li>}
                {!isCountryValid && <li><AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />Country is required.</li>}
                {!isTrandateValid && <li><AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />Transaction date is required.</li>}
                {!isConsultantValid && <li><AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />Consultant is required.</li>}
                {!isReasonValid && <li><AiOutlineWarning style={{ fontSize: 18, marginRight: 6 }} />Reason is required (1–120 chars).</li>}
              </ul>
            ) : (
              <ul className="mr-auto" style={{ margin: 0, padding: 0, color: "green" }}>
                <li><AiOutlineCheckCircle style={{ fontSize: 18, marginRight: 6 }} />Validated</li>
              </ul>
            )}
          </small>
          <Button color="red" onClick={() => setShow(false)} disabled={saving}>Close</Button>
          <Button color="green" onClick={handleSave} disabled={!isFormValid || saving}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </GuardedModal>

      <AddCountryModal
        nested
        hideTrigger
        show={addCountryOpen}
        onHide={() => setAddCountryOpen(false)}
        onCountryCreated={async (data) => {
          await loadDropdownData();
          const id = data?.country_id;
          if (id) setCountry(id);
          setAddCountryOpen(false);
        }}
      />
    </>
  );
}

export default EditCashModal;
